// Melt-and-pour batches: weigh a premade base plus fragrance, colour and additives.
// Recipes are stored in the shared schema (base as the single "oil" line, no lye)
// so batches, stock deduction, scaling, sales and labels work unchanged.

export const MP_BASE_TYPES = ['Clear glycerin', 'White (opaque)', 'Goat milk', 'Shea butter', 'Aloe vera', 'Honey', 'Oatmeal', 'Low-sweat clear', 'Other'];

const num = (value, fallback = 0) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};
const round = (value, places = 1) => Math.round(value * 10 ** places) / 10 ** places;

export const DEFAULT_MELT_POUR = {
  name: '',
  notes: '',
  baseType: 'Clear glycerin',
  baseName: '',
  baseIngredientId: '',
  baseCostPerKg: '',
  bars: 10,
  barWeight: 100,
  overagePct: 5,
  fragranceName: '',
  fragranceIngredientId: '',
  fragrancePct: 2,
  fragranceCostPerKg: '',
  extras: [],
  readyDays: 1,
  packagingPerBar: '',
  laborPerBar: '',
  markupPercent: 120,
};

// Fragrance and percentage additives are measured against the base weight, the way
// most melt-and-pour suppliers state their usage rates.
export function calculateMeltPour(input) {
  const errors = [];
  const warnings = [];
  const bars = Math.floor(num(input.bars));
  const barWeight = num(input.barWeight);
  const overagePct = num(input.overagePct);
  const fragrancePct = num(input.fragrancePct);
  if (bars < 1) errors.push('Enter how many bars you want (at least 1).');
  if (barWeight <= 0) errors.push('Enter the weight of one bar in grams.');
  if (overagePct < 0 || overagePct > 25) errors.push('Extra for spills should be between 0% and 25%.');
  if (fragrancePct < 0 || fragrancePct > 10) errors.push('Fragrance must be between 0% and 10% of the base.');

  const extras = (input.extras || []).map((extra, index) => {
    const value = num(extra.value);
    if (value < 0) errors.push(`${extra.name || `Additive ${index + 1}`} cannot be negative.`);
    return { ...extra, mode: extra.mode === 'g' ? 'g' : 'pct', value: Math.max(0, value) };
  });

  const pourWeight = Math.max(0, bars) * Math.max(0, barWeight) * (1 + Math.max(0, overagePct) / 100);
  const pctTotal = Math.max(0, fragrancePct) + extras.filter(e => e.mode === 'pct').reduce((sum, e) => sum + e.value, 0);
  const gramsTotal = extras.filter(e => e.mode === 'g').reduce((sum, e) => sum + e.value, 0);
  const baseGrams = (pourWeight - gramsTotal) / (1 + pctTotal / 100);
  if (!errors.length && baseGrams <= 0) errors.push('The additives weigh more than the whole batch. Reduce them or make more bars.');

  const fragranceGrams = Math.max(0, baseGrams) * Math.max(0, fragrancePct) / 100;
  const extraLines = extras.map(extra => ({ ...extra, grams: extra.mode === 'g' ? extra.value : Math.max(0, baseGrams) * extra.value / 100 }));
  const additivePct = extras.reduce((sum, e) => sum + (e.mode === 'g' ? (baseGrams > 0 ? e.value / baseGrams * 100 : 0) : e.value), 0);

  if (fragrancePct > 3) warnings.push('This fragrance load is higher than many melt-and-pour suppliers recommend (often 1–3% of the base). Check your supplier’s maximum and IFRA certificate, and watch for sweating or soft bars.');
  if (fragrancePct === 0) warnings.push('No fragrance is included. That is fine for unscented bars.');
  if (additivePct > 10) warnings.push('More than 10% additives can stop some bases setting firmly or make them separate. Test a few bars first.');

  const totalGrams = Math.max(0, baseGrams) + fragranceGrams + extraLines.reduce((sum, e) => sum + e.grams, 0);
  return {
    errors, warnings, bars: Math.max(0, bars), barWeight, pourWeight, totalGrams,
    baseGrams: Math.max(0, baseGrams), fragranceGrams, extraLines, fragrancePct, overagePct,
    perBarGrams: bars > 0 ? totalGrams / bars : 0,
  };
}

export function meltPourCosts(input, result) {
  const costOf = (grams, perKg) => grams / 1000 * Math.max(0, num(perKg));
  const lines = [
    { name: input.baseName || `${input.baseType} base`, grams: result.baseGrams, cost: costOf(result.baseGrams, input.baseCostPerKg), priced: num(input.baseCostPerKg) > 0 },
    ...(result.fragranceGrams > 0 ? [{ name: input.fragranceName || 'Fragrance', grams: result.fragranceGrams, cost: costOf(result.fragranceGrams, input.fragranceCostPerKg), priced: num(input.fragranceCostPerKg) > 0 }] : []),
    ...result.extraLines.filter(e => e.grams > 0).map(e => ({ name: e.name || 'Additive', grams: e.grams, cost: costOf(e.grams, e.costPerKg), priced: num(e.costPerKg) > 0 })),
  ];
  const bars = Math.max(1, result.bars);
  const material = lines.reduce((sum, line) => sum + line.cost, 0);
  const packaging = Math.max(0, num(input.packagingPerBar)) * bars;
  const working = Math.max(0, num(input.laborPerBar)) * bars;
  const totalCost = material + packaging + working;
  const perBar = totalCost / bars;
  const markup = Math.max(0, num(input.markupPercent, 120));
  const sellingPrice = Math.round(perBar * (1 + markup / 100) * 100) / 100;
  return { lines, material, packaging, working, totalCost, perBar, markup, sellingPrice, unpriced: lines.filter(l => !l.priced && l.grams > 0).map(l => l.name) };
}

// Shared-schema record: the base is the one 100% "oil" line, additives carry grams, no lye or water.
export function toMeltPourRecipe(input, result, costs, currencyCode = 'INR') {
  const base = {
    name: input.baseName || `${input.baseType} melt-and-pour base`,
    pct: 100,
    weight: round(result.baseGrams, 2),
    ...(input.baseIngredientId ? { id: input.baseIngredientId, firebaseId: input.baseIngredientId, ingredientId: input.baseIngredientId } : {}),
  };
  const link = id => (id ? { id, firebaseId: id, ingredientId: id } : {});
  const additives = [
    ...(result.fragranceGrams > 0 ? [{ name: input.fragranceName || 'Fragrance', type: 'fragrance', grams: round(result.fragranceGrams, 2), amount: round(result.fragranceGrams, 2), unit: 'g', percentOfBase: result.fragrancePct, ...link(input.fragranceIngredientId) }] : []),
    ...result.extraLines.filter(e => e.grams > 0).map(e => ({
      name: e.name || 'Additive', type: e.type || 'additive', grams: round(e.grams, 2), amount: round(e.grams, 2), unit: 'g',
      ...(e.mode === 'pct' ? { percentOfBase: e.value } : {}), ...link(e.ingredientId),
    })),
  ];
  const totalGrams = round(result.totalGrams, 2);
  return {
    schemaVersion: 2,
    recipeType: 'melt_pour',
    name: input.name?.trim() || `Melt & pour - ${input.baseType}`,
    notes: input.notes || '',
    items: [base], oils: [base], perOilDetails: [base],
    totalOilWeight: round(result.baseGrams, 2),
    additives,
    lyeRequired: 0, naohWeight: 0, kohWeight: 0, alkaliWeights: { NaOH: 0, KOH: 0 }, waterWeight: 0,
    superfat: 0, lyeType: 'NaOH', dualLye: false,
    soapWeight: totalGrams, computedBatchMass: totalGrams, batchSize: totalGrams,
    bars: result.bars, barWeight: result.barWeight, cureDays: Math.max(0, Math.round(num(input.readyDays, 1))),
    unit: 'g', currencyCode,
    cost: { material: costs.material, working: costs.working, packaging: costs.packaging },
    totals: { baseCost: costs.material, workingCost: costs.working, packagingCost: costs.packaging, totalCost: costs.totalCost, perBar: costs.perBar, bars: result.bars },
    pricing: { markup: costs.markup, sellingPrice: costs.sellingPrice },
    laborCostPerBar: Math.max(0, num(input.laborPerBar)), packagingCostPerBar: Math.max(0, num(input.packagingPerBar)), markupPercent: costs.markup,
    use: ['body'], skinType: ['all'],
    makePrivate: true, isPublic: false,
    // Calculator inputs, so the recipe reopens exactly as it was entered.
    meltPour: {
      baseType: input.baseType, baseCostPerKg: num(input.baseCostPerKg), overagePct: result.overagePct,
      fragrancePct: result.fragrancePct, fragranceCostPerKg: num(input.fragranceCostPerKg),
      extras: result.extraLines.map(({ grams, ...extra }) => extra),
    },
  };
}

// Reopen a saved melt-and-pour recipe in the calculator.
export function fromMeltPourRecipe(recipe) {
  const saved = recipe.meltPour || {};
  const base = (recipe.items || recipe.oils || [])[0] || {};
  const fragrance = (recipe.additives || []).find(a => a.type === 'fragrance');
  return {
    ...DEFAULT_MELT_POUR,
    name: recipe.name || '',
    notes: recipe.notes || '',
    baseType: saved.baseType || 'Other',
    baseName: base.name || '',
    baseIngredientId: base.ingredientId || base.firebaseId || base.id || '',
    baseCostPerKg: saved.baseCostPerKg || '',
    bars: recipe.bars || DEFAULT_MELT_POUR.bars,
    barWeight: recipe.barWeight || DEFAULT_MELT_POUR.barWeight,
    overagePct: saved.overagePct ?? 0,
    fragranceName: fragrance?.name || '',
    fragranceIngredientId: fragrance?.ingredientId || fragrance?.id || '',
    fragrancePct: saved.fragrancePct ?? (fragrance?.percentOfBase || 0),
    fragranceCostPerKg: saved.fragranceCostPerKg || '',
    // Fixed-gram additives come from the stored additive lines, so a scaled recipe reopens scaled.
    extras: (saved.extras || []).map((extra, index) => {
      const stored = extra.mode === 'g' && (recipe.additives || []).find(a => a.name === (extra.name || 'Additive') && a.percentOfBase === undefined);
      return { key: `saved-${index}`, ...extra, ...(stored ? { value: stored.grams } : {}) };
    }),
    readyDays: recipe.cureDays ?? 1,
    packagingPerBar: recipe.packagingCostPerBar || '',
    laborPerBar: recipe.laborCostPerBar || '',
    markupPercent: recipe.markupPercent ?? recipe.pricing?.markup ?? 120,
  };
}
